import { useCallback, useMemo, useState } from "react";
// REACT QUERY
import { useQuery } from "@tanstack/react-query";
// MUI
import { Box, Grid, Link, Stack, Tooltip } from "@mui/material";
import { GridColDef, GridRenderCellParams, useGridApiRef } from "@mui/x-data-grid-premium";

// API
import { getReviewDetails } from "@/DataPages/api/socialMediaReviews";

// Hooks
import { useLocation } from "@Common/hooks/Location/useLocation";
import { useSocialMediaReviewDetailDialog } from "@DataPages/context/customerRelations/SocialMediaReviewDetailedDialogContext";

// Components
import CommonToolbar from "@Layouts/components/Tables/DataSmartTable/toolbar/CommonToolbar";
import DataSmartTable from "@Layouts/components/Tables/DataSmartTable";
import NoRowsOverlay from "@Layouts/components/Tables/DataSmartTable/additional/NoRowsOverlay";
import EmptyBody from "@/Layouts/components/Basics/EmptyBody";
import { TypographyCell } from "@/Layouts/components/Tables/DataSmartTable/additional/TypographyCell";
import ReviewDetailsDialog from "../components/ReviewDetailsDialog";

// Helpers
import { getCellClassNameFn, isFooterCell } from "@/Common/helpers/Tables";
import { and } from "@Common/helpers/sonar";
import { filterByRating, filterBySearchCriteria } from "@/DataPages/helpers/socialMediaReviews/reviews";

// Constants
import { HEADER_CLASS_NAME, TAILWIND_FOR_DATA_SMART_TABLE } from "@/Constants/tables";
import { tableKeys } from "@/Constants/tableKeys";

// Types
import { IReviewSiteSummary } from "@DataPages/types/socialMediaReviews/model";
import { IDialogContent, IReviewDateRange } from "@DataPages/types/socialMediaReviews/utils";
import { TSelectionItem } from "../interfaces";

// Assets
import empty from "@/assets/images/commons/empty.png";
import emptyTable from "@/assets/images/commons/empty_table.png";
import WarningIconDark from "@/assets/icons/common/warning-icon-dark.svg";

type ISocialMediaReviewProps = Readonly<{
	locationId: string;
	selectedItem: TSelectionItem | null;
	columns: GridColDef[];
	rows: IReviewSiteSummary[];
	isDataPresent: boolean;
	onDemand: boolean;
	selectedDates: IReviewDateRange | null;
}>;

export default function SocialMediaReviewsAssociateTable(props: Readonly<ISocialMediaReviewProps>) {
	const { onDemand, selectedItem, selectedDates } = props;

	// Inner Constants (Do not take out)
	const source = "Associates";

	const dateRange = selectedDates ?? { startDate: selectedItem?.startDateCommissionCycle, endDate: selectedItem?.payPeriodEndDate };

	const apiRef = useGridApiRef();

	const location = useLocation();
	const { locationId, timezone } = location ?? {};
	const { filter, dispatch } = useSocialMediaReviewDetailDialog();

	const [openDialog, setOpenDialog] = useState(false);
	const [dialogContent, setDialogContent] = useState<IDialogContent | null>(null);

	/********************************************* QUERIES ************************************************/
	const { data: reviewDetailsResponse, isLoading: isReviewDetailsLoading } = useQuery({
		queryKey: [
			`${source}-review-details-dialog`,
			locationId,
			dialogContent?.columnName,
			dialogContent?.reputationMgmtUserId,
			dateRange?.startDate,
			dateRange?.endDate,
		],
		queryFn: () =>
			getReviewDetails(locationId as string, selectedItem?.commissionCycleId as string, {
				timezone,
				dateRange,
				reputationMgmtUserId: dialogContent?.reputationMgmtUserId as string,
				siteName: dialogContent?.columnName as string,
				source: "users",
			}),
		enabled: and(
			Boolean(locationId),
			Boolean(selectedItem?.commissionCycleId),
			Boolean(dialogContent?.reputationMgmtUserId),
			Boolean(dialogContent?.columnName),
			openDialog
		),
		staleTime: Infinity,
		gcTime: 5 * 60 * 1000, // 5 Min
	});

	const renderDialogCellContent = (
		params: GridRenderCellParams,
		handleOpenDialog: (content: string, employeeId: string, reputationMgmtUserId: string, columnName: string) => void,
		onDemand: boolean
	) => {
		const content = params.row[params.field];
		const totalReviews = content?.totalReviews;
		const needResponse = content?.neededResponseTotal;

		if (!totalReviews && typeof totalReviews !== "number") {
			return <TypographyCell fontSize="12px">{totalReviews}</TypographyCell>;
		}

		const noun = needResponse === 1 ? "Review" : "Reviews";
		const verb = needResponse === 1 ? "needs" : "need";

		return (
			<TypographyCell fontSize="12px" hasTooltip={false}>
				{onDemand ? (
					totalReviews
				) : (
					<>
						<Stack direction={"row"} justifySelf={"end"} spacing={1}>
							{Boolean(needResponse) && (
								<Tooltip title={`${needResponse} ${noun} ${verb} response`} arrow>
									<img src={WarningIconDark} alt="warning-icon" />
								</Tooltip>
							)}
							<Box width={15}>
								<Link
									component="button"
									style={{ color: "#2357be" }}
									// Callbacks
									onClick={() =>
										handleOpenDialog(
											totalReviews,
											params.row.employee_id as string,
											params.row.reputationMgmtUserId as string,
											params.field
										)
									}
								>
									{totalReviews}
								</Link>
							</Box>
						</Stack>
					</>
				)}
			</TypographyCell>
		);
	};

	/********************************************* HANDLERS ************************************************/
	const handleOpenDialog = useCallback((content: string, employeeId: string, reputationMgmtUserId: string, columnName: string) => {
		setOpenDialog(true);
		setDialogContent({
			content,
			employeeId,
			reputationMgmtUserId,
			columnName,
		});
	}, []);

	const handleCloseDialog = useCallback(() => {
		setOpenDialog(false);
		setDialogContent(null);
		dispatch({ type: "RESET" });
	}, [dispatch]);

	const transformColumns = useCallback(
		(columns: GridColDef[]): GridColDef[] => {
			return columns.map((column: GridColDef) => {
				return {
					...column,
					resizable: false,
					headerClassName: `${column.headerClassName} [&_.MuiDataGrid-columnHeaderTitle]:!text-[12px] ${HEADER_CLASS_NAME}`,
					cellClassName: getCellClassNameFn([], {}, { footer: "!px-[12px]" }),
					valueGetter: (value: { totalReviews: number }) => {
						return typeof value === "object" ? value?.totalReviews : value;
					},
					renderCell: (params) => {
						if (isFooterCell(params)) {
							return (
								<TypographyCell fontSize="14px" hints={{ isFooter: true, noWrap: true, noFocus: true }}>
									{params.value ?? "Total"}
								</TypographyCell>
							);
						}

						if (["name", "employee_id", "_id"].includes(params.field)) {
							return (
								<TypographyCell hasTooltip={false} fontSize="12px">
									{params.value}
								</TypographyCell>
							);
						}

						return renderDialogCellContent(params, handleOpenDialog, onDemand);
					},
				} as GridColDef;
			});
		},
		[onDemand, handleOpenDialog]
	);

	const arrayData = reviewDetailsResponse?.reviewDetails;
	const filteredData = useMemo(() => {
		if (!arrayData) {
			return [];
		}

		let result = [...arrayData];

		if (filter.ratings) {
			result = filterByRating(result, filter.ratings);
		}

		if (filter.text) {
			result = filterBySearchCriteria(result, filter.text);
		}

		return result;
	}, [arrayData, filter.ratings, filter.text]);

	if (!props?.rows?.length) {
		return (
			<Grid item xs={12}>
				<EmptyBody
					image={props.selectedItem ? emptyTable : empty}
					imageStyles={{ width: "250px" }}
					title={props.selectedItem ? "No Social Media Reviews found" : "Select a month to view their Social Media Reviews"}
					bodyHeightClassName="!h-[400px]"
					sx={{
						boxShadow: "none !important",
						height: "auto !important",
					}}
				/>
			</Grid>
		);
	}

	if (!props?.columns?.length || !props.isDataPresent) {
		return <EmptyBody image={emptyTable} title="No columns found" bodyHeightClassName="!h-[400px]" />;
	}

	return (
		<>
			<DataSmartTable
				filterStoreConfig={{
					key: tableKeys.DATA_PAGES.SOCIAL_MEDIA_REVIEWS,
				}}
				apiRef={apiRef}
				columns={transformColumns(props.columns)}
				rows={props?.rows ?? []}
				maxRows={10}
				autoHeight={false}
				enableCustomScrollbars
				disableColumnMenu
				disableRowSelectionOnClick
				disableMultipleRowSelection
				checkboxSelection={false}
				columnVisibilityModel={{
					_id: false,
					employee_id: false,
				}}
				initialState={{
					aggregation: {
						model: {
							betterbusinessbureau: "sum",
							cars: "sum",
							cargurus: "sum",
							carfax: "sum",
							dealerrater: "sum",
							edmunds: "sum",
							facebook: "sum",
							google: "sum",
							review: "sum",
						},
					},
				}}
				slots={{
					toolbar: CommonToolbar,
					noRowsOverlay: NoRowsOverlay,
				}}
				slotProps={{
					toolbar: {
						switch: {
							hide: true,
						},
						filter: {
							hide: false,
						},
						search: {
							useQuickFilter: true,
						},
						add: {
							hide: true,
							disabled: true,
						},
						delete: {
							deleteTitle: "Delete",
							hide: true,
						},
						expand: {
							hide: true,
						},
					},
					noRowsOverlay: {
						title: "No Social Media Reviews Data",
					},
				}}
				hideFooter
				customProps={{
					tableClassName: {
						"functional-table footer-table-included": true,
						...TAILWIND_FOR_DATA_SMART_TABLE,
						"[&_.toolbar-actions]:!top-[0px]": true,
						"[&_.full-collapse-toolbar]:!h-[33px]": true,
						"[&_.MuiDataGrid-row--lastVisible_.MuiDataGrid-scrollbarFiller--borderTop]:!bg-primary": true,
						"[&_.MuiDataGrid-row--lastVisible_.MuiDataGrid-scrollbarFiller--borderTop]:!border-primary": true,
						"[&_.MuiDataGrid-main]:mt-[16px] z-0": true,
					},
					aggregationFooterClassName: "aggregation-footer regular-footer",
					expandableRows: false,
					hide: false,
				}}
			/>

			<ReviewDetailsDialog
				open={openDialog}
				isLoading={isReviewDetailsLoading}
				dialogContent={dialogContent}
				totalReviewAmount={arrayData?.length ?? 0}
				detailedReviews={filteredData}
				source={source}
				// Callbacks
				onClose={handleCloseDialog}
			/>
		</>
	);
}
